import { ChangeEvent, ReactNode, useRef, useState } from 'react';
import { Avatar, Box, Button, CircularProgress, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import EditIcon from '@mui/icons-material/Edit';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { validateImageFile } from '@/shared/lib/validateImageFile';
import { CameraPhotoDialog } from './CameraPhotoDialog';

type Variant = 'overlay' | 'button';

export interface ImageUploadProps {
  imageUrl: string | null | undefined;
  fallback?: ReactNode;
  fallbackBgColor?: string;
  fallbackTextColor?: string;
  size?: number;
  variant?: Variant;
  allowedTypes?: string[];
  maxSizeBytes?: number;
  uploadLabel?: string;
  changeLabel?: string;
  helperText?: string;
  allowCamera?: boolean;
  isUploading?: boolean;
  onSelect: (file: File) => void;
  imageAlt?: string;
  ariaLabel?: string;
  dataTestId?: string;
}

const DEFAULT_ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

export const ImageUpload = ({
  imageUrl,
  fallback,
  fallbackBgColor,
  fallbackTextColor,
  size = 120,
  variant = 'button',
  allowedTypes = DEFAULT_ALLOWED_TYPES,
  maxSizeBytes,
  uploadLabel = 'Upload',
  changeLabel = 'Change',
  helperText,
  allowCamera = false,
  isUploading = false,
  onSelect,
  imageAlt,
  ariaLabel,
  dataTestId,
}: ImageUploadProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const acceptAttr = allowedTypes.join(',');
  const hasImage = Boolean(imageUrl);
  const buttonLabel = hasImage ? changeLabel : uploadLabel;
  const menuOpen = Boolean(menuAnchor);

  const openFilePicker = () => {
    inputRef.current?.click();
  };

  const handleTriggerClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isUploading) {
      return;
    }
    if (allowCamera) {
      setMenuAnchor(event.currentTarget);
      return;
    }
    openFilePicker();
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleUploadFromDevice = () => {
    handleMenuClose();
    openFilePicker();
  };

  const handleTakePhoto = () => {
    handleMenuClose();
    setCameraOpen(true);
  };

  const handleSelect = (file: File) => {
    const result = validateImageFile(file, { allowedTypes, maxSizeBytes });
    if (!result.valid) {
      setError(result.error);
      return;
    }
    setError(undefined);
    onSelect(file);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleSelect(file);
    }
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleCameraCapture = (file: File) => {
    handleSelect(file);
  };

  const preview = (
    <Box sx={{ position: 'relative', width: size, height: size }}>
      <Avatar
        src={imageUrl ?? undefined}
        alt={hasImage ? (imageAlt ?? ariaLabel ?? 'Image preview') : ''}
        sx={{
          width: size,
          height: size,
          bgcolor: hasImage ? undefined : (fallbackBgColor ?? 'grey.100'),
          color: fallbackTextColor ?? 'text.secondary',
          fontSize: `${size * 0.25}px`,
        }}
      >
        {!hasImage && (fallback ?? <CloudUploadIcon sx={{ fontSize: size * 0.35, color: 'grey.400' }} />)}
      </Avatar>
      {isUploading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            bgcolor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress size={Math.max(24, size * 0.35)} sx={{ color: 'common.white' }} />
        </Box>
      )}
    </Box>
  );

  const hiddenInput = (
    <input
      ref={inputRef}
      type="file"
      accept={acceptAttr}
      onChange={handleFileChange}
      style={{ display: 'none' }}
      aria-label={ariaLabel ?? uploadLabel}
      disabled={isUploading}
      data-testid={dataTestId}
    />
  );

  const triggerAriaLabel = isUploading ? 'Uploading...' : buttonLabel;
  const iconButtonSize = Math.max(28, size * 0.25);
  const iconSize = Math.max(14, size * 0.15);

  return (
    <Box
      sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: variant === 'button' ? 2 : 0 }}
    >
      {hiddenInput}

      {variant === 'overlay' ? (
        <Box sx={{ position: 'relative', display: 'inline-block' }}>
          <Box
            onClick={handleTriggerClick}
            aria-hidden="true"
            sx={{
              cursor: isUploading ? 'not-allowed' : 'pointer',
              opacity: isUploading ? 0.7 : 1,
              transition: 'opacity 0.2s',
              '&:hover': { opacity: isUploading ? 0.7 : 0.9 },
              borderRadius: '50%',
            }}
          >
            {preview}
          </Box>
          <IconButton
            onClick={handleTriggerClick}
            disabled={isUploading}
            aria-label={triggerAriaLabel}
            size="small"
            sx={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              width: iconButtonSize,
              height: iconButtonSize,
              bgcolor: 'background.paper',
              border: '2px solid',
              borderColor: 'divider',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <CameraAltIcon sx={{ fontSize: iconSize }} />
          </IconButton>
        </Box>
      ) : (
        <>
          {preview}
          <Button
            variant={hasImage ? 'text' : 'outlined'}
            startIcon={hasImage ? <EditIcon /> : <CloudUploadIcon />}
            onClick={handleTriggerClick}
            disabled={isUploading}
            aria-label={buttonLabel}
          >
            {buttonLabel}
          </Button>
        </>
      )}

      {helperText && (
        <Typography variant="caption" color="text.secondary">
          {helperText}
        </Typography>
      )}

      {error && (
        <Typography color="error" variant="body2" role="alert">
          {error}
        </Typography>
      )}

      {allowCamera && (
        <Menu anchorEl={menuAnchor} open={menuOpen} onClose={handleMenuClose}>
          <MenuItem onClick={handleUploadFromDevice}>
            <CloudUploadIcon fontSize="small" sx={{ mr: 1 }} />
            Upload from device
          </MenuItem>
          <MenuItem onClick={handleTakePhoto}>
            <PhotoCameraIcon fontSize="small" sx={{ mr: 1 }} />
            Take photo
          </MenuItem>
        </Menu>
      )}

      {allowCamera && (
        <CameraPhotoDialog open={cameraOpen} onClose={() => setCameraOpen(false)} onCapture={handleCameraCapture} />
      )}
    </Box>
  );
};
